import { Resource, ResourceMeta } from '@dispatch/shared/types';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import { grey } from '@mui/material/colors';
import { Control, FieldValues, UseFormGetValues } from 'react-hook-form';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { useNotification } from '@dispatch/web/hooks';
import { ResourceDataGrid } from '@dispatch/web/ui';

interface ResourceList {
  data: any;
  control: Control<FieldValues>;
  getValues?: UseFormGetValues<FieldValues>;
  handleReassign: ({
    resourceId,
    reasonCode,
    forceReassign,
  }: {
    resourceId: string;
    reasonCode: string;
    forceReassign: boolean;
  }) => void;
  resourceMeta?: ResourceMeta;
}

export const ResourceList = ({
  data,
  getValues,
  handleReassign,
  resourceMeta,
}: ResourceList) => {
  const [showList, setShowList] = React.useState<boolean>(false);
  const currentAssignedResource = getValues?.('resource');
  const { notifyError } = useNotification();

  const resources = React.useMemo(() => {
    // dont show currently assigned resource in the list
    const resourceData = data?.data?.filter(
      (resource: Resource & { id: string }) => {
        const resourceId = resource?.integrationGUID ?? resource?.id;

        return currentAssignedResource?.integrationGUID
          ? resourceId !== currentAssignedResource?.integrationGUID
          : resourceId !== currentAssignedResource?.id;
      }
    );

    return {
      ...data,
      data: resourceData,
    };
  }, [
    currentAssignedResource?.integrationGUID,
    currentAssignedResource?.Id,
    data,
  ]);

  return (
    <div style={{ marginTop: '25px' }}>
      <Grid
        data-testid="available-resource-dropdown"
        container
        alignItems="center"
        padding="10px 16px"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : 'white',
          cursor: 'pointer',
        })}
        onClick={() => setShowList(!showList)}
      >
        <Grid item sm={6}>
          <Typography component="p" fontWeight="700" fontSize={14}>
            Available Resources
          </Typography>
        </Grid>
        <Grid item sm={6}>
          <Stack direction={'row'} justifyContent="end">
            <Button
              variant="text"
              onClick={() => setShowList(!showList)}
              data-testid="show-resources-button"
            >
              {showList ? (
                <ExpandLessIcon sx={{ color: grey[600] }} />
              ) : (
                <ExpandMoreIcon sx={{ color: grey[600] }} />
              )}
            </Button>
          </Stack>
        </Grid>
      </Grid>
      {showList ? (
        <div
          style={{ borderTop: `1px solid ${grey[300]}`, height: '250px' }}
          data-testid="task-resources-list"
        >
          <ResourceDataGrid
            resources={resources}
            resourceMeta={{
              ...resourceMeta,
              notifyError,
              handleReassign,
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
